import { Contract } from '@ethersproject/contracts'
import type { Provider } from '@ethersproject/abstract-provider'

// Authoritative, trust-minimized balance reads for the Robinhood holdings tray. Blockscout is only
// used to DISCOVER which token contracts a wallet touches (discovery.ts); the balances a user sees and
// (potentially) acts on are read here, on-chain, via the canonical Multicall3 (deployed on 4663). Each
// token call is `allowFailure`, so one broken/hostile contract can never fail the whole batch.

/** Canonical Multicall3, deployed at the same address on 4663 (verified on-chain). */
export const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11'

const MULTICALL3_AGGREGATE3_ABI = [
  {
    type: 'function',
    name: 'aggregate3',
    stateMutability: 'view',
    inputs: [
      {
        name: 'calls',
        type: 'tuple[]',
        components: [
          { name: 'target', type: 'address' },
          { name: 'allowFailure', type: 'bool' },
          { name: 'callData', type: 'bytes' },
        ],
      },
    ],
    outputs: [
      {
        name: 'returnData',
        type: 'tuple[]',
        components: [
          { name: 'success', type: 'bool' },
          { name: 'returnData', type: 'bytes' },
        ],
      },
    ],
  },
] as const

const BALANCE_OF_SELECTOR = '0x70a08231' // balanceOf(address)
const DECIMALS_SELECTOR = '0x313ce567' // decimals()

function balanceOfCalldata(account: string): string {
  return BALANCE_OF_SELECTOR + account.toLowerCase().replace(/^0x/, '').padStart(64, '0')
}

/** Parse a 32-byte uint return into bigint; undefined for empty/short data. */
function decodeUint(returnData: string | undefined): bigint | undefined {
  if (typeof returnData !== 'string' || returnData === '0x' || returnData.length < 3) return undefined
  try {
    return BigInt(returnData.length > 66 ? '0x' + returnData.slice(2, 66) : returnData)
  } catch {
    return undefined
  }
}

type MulticallResult = ReadonlyArray<{ success: boolean; returnData: string }>

async function aggregate3(
  provider: Provider,
  calls: Array<{ target: string; allowFailure: boolean; callData: string }>,
  batchSize: number,
): Promise<MulticallResult> {
  const mc = new Contract(MULTICALL3_ADDRESS, MULTICALL3_AGGREGATE3_ABI, provider)
  const all: Array<{ success: boolean; returnData: string }> = []
  for (let i = 0; i < calls.length; i += batchSize) {
    const chunk = calls.slice(i, i + batchSize)
    const res = (await mc.callStatic.aggregate3(chunk)) as MulticallResult
    for (const r of res) all.push({ success: r.success, returnData: r.returnData })
  }
  return all
}

/** Reads `balanceOf(account)` for each token via Multicall3 (bounded batches). Missing/failed reads are
 * omitted (never assumed). Returns lowercase-address → raw balance. */
export async function readBalances(
  provider: Provider,
  account: string,
  tokenAddresses: string[],
  batchSize = 100,
): Promise<Map<string, bigint>> {
  const out = new Map<string, bigint>()
  if (tokenAddresses.length === 0) return out
  const calls = tokenAddresses.map((t) => ({ target: t, allowFailure: true, callData: balanceOfCalldata(account) }))
  const res = await aggregate3(provider, calls, batchSize)
  res.forEach((r, i) => {
    if (!r.success) return
    const bal = decodeUint(r.returnData)
    if (bal !== undefined) out.set(tokenAddresses[i].toLowerCase(), bal)
  })
  return out
}

/** Reads `decimals()` for each token via Multicall3 — used only for tokens whose decimals aren't
 * curated. Returns lowercase-address → decimals (0..36, else omitted). */
export async function readDecimals(
  provider: Provider,
  tokenAddresses: string[],
  batchSize = 100,
): Promise<Map<string, number>> {
  const out = new Map<string, number>()
  if (tokenAddresses.length === 0) return out
  const calls = tokenAddresses.map((t) => ({ target: t, allowFailure: true, callData: DECIMALS_SELECTOR }))
  const res = await aggregate3(provider, calls, batchSize)
  res.forEach((r, i) => {
    if (!r.success) return
    const dec = decodeUint(r.returnData)
    if (dec !== undefined && dec <= 36n) out.set(tokenAddresses[i].toLowerCase(), Number(dec))
  })
  return out
}
